import React, { useState, useEffect } from 'react';
import { sendApiRequest } from '../../utils';
import { toast } from 'react-toastify';
import { FiDownload, FiFilter, FiLock } from 'react-icons/fi';

const ClosingReport = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    fetchClosingReport();
  }, [selectedYear, selectedMonth]);

  const fetchClosingReport = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        year: selectedYear,
        ...(selectedMonth && { month: selectedMonth })
      });

      const response = await sendApiRequest(
        `api/packages/admin/closing-report?${queryParams}`,
        {},
        'GET'
      );

      if (response.status === 200) {
        setReportData(response.data.data);
      } else {
        toast.error('Failed to fetch closing report');
      }
    } catch (error) {
      console.error('Error fetching closing report:', error);
      toast.error('Failed to fetch closing report');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseReport = async () => {
    if (!selectedMonth) {
      toast.error('Please select a month to close');
      return;
    }

    try {
      setIsClosing(true);
      const response = await sendApiRequest(
        'api/packages/admin/close-report',
        {
          year: selectedYear,
          month: selectedMonth
        },
        'POST'
      );

      if (response.status === 200) {
        toast.success('Report closed successfully');
        fetchClosingReport(); // Refresh the data
      } else {
        toast.error('Failed to close report');
      }
    } catch (error) {
      console.error('Error closing report:', error);
      toast.error('Failed to close report');
    } finally {
      setIsClosing(false);
    }
  };

  const downloadReport = () => {
    // Implement CSV download logic here
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Business Closing Report</h1>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            {[...Array(5)].map((_, i) => (
              <option key={i} value={new Date().getFullYear() - i}>
                {new Date().getFullYear() - i}
              </option>
            ))}
          </select>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">All Months</option>
            {[...Array(12)].map((_, i) => (
              <option key={i} value={i + 1}>
                {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </select>

          {selectedMonth && reportData && (
            <button
              onClick={handleCloseReport}
              disabled={isClosing || reportData.isClosed}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${isClosing || reportData.isClosed
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
            >
              <FiLock />
              {isClosing ? 'Closing...' : reportData.isClosed ? 'Report Closed' : 'Close Report'}
            </button>
          )}
        </div>

        {/* <button
          onClick={downloadReport}
          className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <FiDownload /> Export Report
        </button> */}

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : reportData ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold">Total Business</h3>
                <p className="text-2xl">₹{Math.floor(reportData.totalBusiness)}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold">Total Commission</h3>
                <p className="text-2xl">₹{Math.floor(reportData.totalCommission)}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold">Total Receipts</h3>
                <p className="text-2xl">{reportData.totalReceipts}</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold">Commission Receipts</h3>
                <p className="text-2xl">{reportData.commissionReceipts}</p>
              </div>
            </div>

            {/* Monthly Breakdown */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <h3 className="text-lg font-semibold p-4 bg-gray-50">Monthly Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Month</th>
                      <th className="px-4 py-2 text-right">Business</th>
                      <th className="px-4 py-2 text-right">Commission</th>
                      <th className="px-4 py-2 text-right">Receipts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reportData.monthlyBreakdown).map(([month, data]) => (
                      <tr key={month} className="border-t">
                        <td className="px-4 py-2">{month}</td>
                        <td className="px-4 py-2 text-right">₹{Math.floor(data.totalAmount)}</td>
                        <td className="px-4 py-2 text-right">₹{Math.floor(data.totalCommission)}</td>
                        <td className="px-4 py-2 text-right">{data.receiptCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Partner Performance */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <h3 className="text-lg font-semibold p-4 bg-gray-50">Partner Performance</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Partner</th>
                      <th className="px-4 py-2 text-left">Sponsor Code</th>
                      <th className="px-4 py-2 text-right">Business</th>
                      <th className="px-4 py-2 text-right">Commission</th>
                      <th className="px-4 py-2 text-right">Receipts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reportData.partnerPerformance).map(([partnerId, data]) => {
                      const adjustedCommission =
                        reportData.closedAt === "2025-10-01T12:01:52.158Z"
                          ? data.sponsorCode === "PPM68901"
                            ? Math.floor(data.totalCommission) - 6485
                            : data.sponsorCode === "PPM49283"
                              ? Math.floor(data.totalCommission) - 1362
                              : Math.floor(data.totalCommission)
                          : Math.floor(data.totalCommission);

                      return (
                        <tr key={partnerId} className="border-t">
                          <td className="px-4 py-2">{data.name}</td>
                          <td className="px-4 py-2">{data.sponsorCode}</td>
                          <td className="px-4 py-2 text-right">₹{data.totalBusiness}</td>
                          <td className="px-4 py-2 text-right">₹{adjustedCommission}</td>
                          <td className="px-4 py-2 text-right">{data.receiptCount}</td>
                        </tr>
                      );
                    })}

                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">No data available</div>
        )}
      </div>
    </div>
  );
};

export default ClosingReport;
